import { IAdmin } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, Select, FormInstance } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { FormAction } from "src/Components/FormAction";
import { PATH_ADMINS, userRoles } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";
import { authApi } from "@api";
import { useNotify } from "@utils";
import { useHistory } from "react-router";

interface IProps {
  admin?: IAdmin;
}

const initValue: Partial<IAdmin> = {
  name: "",
  email: "",
};

export const AdminForm = memo(({ admin }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupAdmin } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const clientSchema = Yup.object().shape(YupAdmin);
  const [data, setData] = useState<Partial<IAdmin>>(initValue);
  const [clientForm] = Form.useForm();

  useEffect(() => {
    if (admin) {
      setData({ ...data, ...admin });
      formRef.current?.setFieldsValue(admin);
    }
  }, [admin]);

  const onFinish = async (values: IAdmin) => {
    try {
      dispatch(setLoading(true));
      if (admin) {
        await authApi.update({ ...data, ...values }, data?.userId || "");
      } else {
        await authApi.create({ ...data, ...values });
      }
      success(t("message.create.success"));
      history.push(PATH_ADMINS);
    } catch (err: any) {
      error(err.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await clientSchema.validateSyncAt(field, { [field]: value });
    },
  };
  return (
    <Form
      form={clientForm}
      name="basic"
      ref={formRef}
      initialValues={data}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.user_name")} name="name" rules={[yupSync]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.email")} name="email" rules={[yupSync]}>
            <Input type="email" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.user_role")} name="userRole" rules={[yupSync]}>
            <Select placeholder={t("page.selected_user_role")}>
              {userRoles.map((item) => (
                <Select.Option key={item} value={item}>
                  {t(`page.${item}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <FormAction />
    </Form>
  );
});
